import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, MapPin, Linkedin, Facebook, Instagram, Twitter } from "lucide-react";
import Logo from "@/components/Logo";
import { COMPANY, SERVICES, NAV_LINKS, SOCIAL } from "@/lib/data";

const SOCIAL_ICONS = { linkedin: Linkedin, facebook: Facebook, instagram: Instagram, x: Twitter };

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="footer-reference" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Logo light testid="footer-logo" />
            <p className="mt-6 text-slate-300 leading-relaxed max-w-sm text-[15px]">
              A licensed California freight company. Port drayage, full truckload, LTL,
              refrigerated, flatbed, warehousing and expedited — with 24/7 dispatch on every load.
            </p>
            <div className="mt-8 space-y-3 text-[15px]">
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="footer-email-link"
                className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors w-fit"
              >
                <span className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center"><Mail className="h-4 w-4 text-white" /></span> {COMPANY.email}
              </a>
              <a
                href={`tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`}
                data-testid="footer-phone-link"
                className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors w-fit"
              >
                <span className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center"><Phone className="h-4 w-4 text-white" /></span> {COMPANY.phone} — 24/7
              </a>
              <p className="flex items-center gap-3 text-slate-300" data-testid="footer-location">
                <span className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center"><MapPin className="h-4 w-4 text-white" /></span> {COMPANY.location}
              </p>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="label text-white mb-5">Company</p>
            <ul className="space-y-3 text-[15px]">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase()}`} className="text-slate-300 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/quote" data-testid="footer-link-quote" className="text-slate-300 hover:text-white transition-colors">
                  Get a Quote
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-5">
            <p className="label text-white mb-5">Services</p>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3 text-[15px]">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`footer-service-${s.slug}`}
                    className="text-slate-300 hover:text-white transition-colors"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/quote"
              data-testid="footer-cta-link"
              className="group btn-pill primary-action mt-8 px-6 py-3 hover:-translate-y-0.5 hover:shadow-glow"
            >
              Move freight with us
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <p className="text-slate-400 text-sm" data-testid="footer-copyright">
            © {year} {COMPANY.name}. All rights reserved. Licensed &amp; bonded.
          </p>
          <div className="flex flex-wrap items-center gap-5">
            <Link to="/privacy" data-testid="footer-link-privacy" className="text-sm text-slate-300 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/admin" data-testid="footer-owner-login" className="text-sm text-slate-300 hover:text-white transition-colors">Owner login</Link>
            <div className="flex items-center gap-2" data-testid="footer-social-links">
              {SOCIAL.map((s) => {
                const Icon = SOCIAL_ICONS[s.key];
                return (
                  <a
                    key={s.key}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    data-testid={`footer-social-${s.key}`}
                    className="h-11 w-11 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-white hover:bg-white/15 transition-colors"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
